import React from 'react';

export interface FilterState {
  preset: string;
  brightness: number;  // -100 to 100
  contrast: number;    // -100 to 100
  saturation: number;  // -100 to 100
}

interface FiltersPanelProps {
  filters: FilterState;
  onChange: (filters: FilterState) => void;
  onClose: () => void;
}

export const FILTER_PRESETS: { name: string; css: string }[] = [
  { name: 'normal', css: 'none' },
  { name: 'warm', css: 'sepia(0.2) saturate(1.3) brightness(1.05)' },
  { name: 'cool', css: 'hue-rotate(15deg) saturate(0.9) brightness(1.05)' },
  { name: 'bw', css: 'grayscale(1)' },
  { name: 'sepia', css: 'sepia(0.8)' },
  { name: 'vivid', css: 'saturate(1.6) contrast(1.1)' },
  { name: 'retro', css: 'sepia(0.3) contrast(1.2) brightness(0.9)' },
  { name: 'cinema', css: 'contrast(1.2) brightness(0.95) saturate(0.85)' },
  { name: 'neon', css: 'saturate(2) brightness(1.1) contrast(1.15) hue-rotate(-10deg)' },
];

export function buildFilterCSS(filters: FilterState): string {
  const preset = FILTER_PRESETS.find(p => p.name === filters.preset);
  const parts: string[] = [];
  if (preset && preset.css !== 'none') parts.push(preset.css);
  if (filters.brightness !== 0) parts.push(`brightness(${1 + filters.brightness / 100})`);
  if (filters.contrast !== 0) parts.push(`contrast(${1 + filters.contrast / 100})`);
  if (filters.saturation !== 0) parts.push(`saturate(${1 + filters.saturation / 100})`);
  return parts.length > 0 ? parts.join(' ') : 'none';
}

const PRESET_LABELS: Record<string, string> = {
  normal: 'Normal', warm: 'Warm', cool: 'Cool', bw: 'B&W',
  sepia: 'Sepia', vivid: 'Vivid', retro: 'Retro', cinema: 'Cinema', neon: 'Neon',
};

const FiltersPanel: React.FC<FiltersPanelProps> = ({ filters, onChange, onClose }) => {
  const handlePreset = (name: string) => {
    onChange({ ...filters, preset: name });
  };

  const handleSlider = (key: 'brightness' | 'contrast' | 'saturation', value: number) => {
    onChange({ ...filters, [key]: value });
  };

  const handleReset = () => {
    onChange({ preset: 'normal', brightness: 0, contrast: 0, saturation: 0 });
  };

  return (
    <div className="filters-panel">
      <div className="panel-header">
        <h3>Filters</h3>
        <button className="btn-icon" onClick={onClose}>✕</button>
      </div>

      <div className="filter-presets-grid">
        {FILTER_PRESETS.map(p => (
          <button
            key={p.name}
            className={`filter-preset-card ${filters.preset === p.name ? 'active' : ''}`}
            onClick={() => handlePreset(p.name)}
          >
            <div className="filter-preview" style={{ filter: p.css }} />
            <span>{PRESET_LABELS[p.name]}</span>
          </button>
        ))}
      </div>

      <div className="filter-sliders">
        <div className="control-row">
          <label>Brightness</label>
          <input
            type="range" min="-100" max="100" step="1"
            value={filters.brightness}
            onChange={(e) => handleSlider('brightness', parseInt(e.target.value))}
          />
          <span>{filters.brightness > 0 ? '+' : ''}{filters.brightness}</span>
        </div>
        <div className="control-row">
          <label>Contrast</label>
          <input
            type="range" min="-100" max="100" step="1"
            value={filters.contrast}
            onChange={(e) => handleSlider('contrast', parseInt(e.target.value))}
          />
          <span>{filters.contrast > 0 ? '+' : ''}{filters.contrast}</span>
        </div>
        <div className="control-row">
          <label>Saturation</label>
          <input
            type="range" min="-100" max="100" step="1"
            value={filters.saturation}
            onChange={(e) => handleSlider('saturation', parseInt(e.target.value))}
          />
          <span>{filters.saturation > 0 ? '+' : ''}{filters.saturation}</span>
        </div>
      </div>

      <button className="btn-sm filter-reset-btn" onClick={handleReset}>Reset All</button>
    </div>
  );
};

export default FiltersPanel;
